import { useCallAPI } from 'hooks/useCallAPI';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pmp, PmpFormType, PmpType } from 'core/pmp/Pmp';
import i18n from 'i18n';
import moment from 'moment';
import { SummaryData } from 'components/SummaryDetail/SummaryDetail';
import { useParams } from 'react-router-dom';
import { DefaultPmpManager } from 'core/pmp/PmpManager';
import { usePmpSummaryModel } from './PmpSummaryModel';
import { toast } from 'react-toastify';
import { noop } from 'lodash';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

const pmpManager = new DefaultPmpManager();

export type PmpSetupFlowModel = {
  type: 'create' | 'edit' | 'summary';
  pmp?: PmpFormType;
  title: string;
  loading: boolean;
  summaryData: SummaryData[];
  redirectPath?: string;
  defaultPmp?: Pmp;
  readonly: boolean;
  haveBoundCampaign: boolean;
  setPmp: (pmp: PmpFormType) => void;
  handleSubmit: (pmp: PmpFormType) => void;
  backToList: () => void;
};

const PIC_CHANNEL_ID = 2;

export const useCreatePmpSetupFlowPageModel = (channelSpaceOptionsMap: {
  [channelId: number]: SelectOptions[];
}): PmpSetupFlowModel => {
  const { loading, callAPIs } = useCallAPI();
  const [redirectPath, setRedirectPath] = useState<string | undefined>();

  const pmpChannelIds = useMemo(
    () =>
      // ECOPD-5566：停用渠道的 PMP 選項強制關閉
      ChannelGroupSetting.activeValues()
        .filter((setting) => setting.config.addonFeatures.campaign.supportPMP)
        .map((setting) => setting.config.id),
    []
  );

  const initPmp: PmpFormType = useMemo(
    () => ({
      name: '',
      adAgencyIds: [],
      startTime: moment()
        .startOf('day')
        .add(1, 'days')
        .format('YYYY-MM-DD HH:mm:ss'),
      endTime: moment()
        .startOf('day')
        .add(1, 'days')
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
      pmpType: PmpType.BUYOUT,
      budget: 1,
      spaces: [],
      channelGroupId: pmpChannelIds[0]
    }),
    [pmpChannelIds]
  );

  const [pmp, setPmp] = useState<PmpFormType | undefined>(initPmp);

  const spaceOptions = useMemo(() => {
    return pmp ? channelSpaceOptionsMap[pmp.channelGroupId] || [] : [];
  }, [pmp, channelSpaceOptionsMap]);

  const summaryData = usePmpSummaryModel(pmp, spaceOptions);

  const backToList = useCallback(() => {
    setRedirectPath('/pmp');
  }, []);

  const handleSubmit = useCallback(
    (pmp: PmpFormType) => {
      callAPIs([() => pmpManager.createPmp(pmp)], () => {
        backToList();
        toast.success(i18n.t('pmpSetupFlowPage.labels.createSuccess'));
      });
    },
    [callAPIs, backToList]
  );

  return {
    type: 'create',
    pmp,
    title: i18n.t('pmpSetupFlowPage.titles.create'),
    loading: loading,
    redirectPath,
    summaryData,
    readonly: false,
    haveBoundCampaign: false,
    setPmp,
    backToList,
    handleSubmit
  };
};

export const useEditPmpSetupFlowPageModel = (channelSpaceOptionsMap: {
  [channelId: number]: SelectOptions[];
}): PmpSetupFlowModel => {
  const { loading, callAPIs } = useCallAPI();
  const [redirectPath, setRedirectPath] = useState<string | undefined>();
  const { pmpId } = useParams<{ pmpId: string }>();
  const [pmp, setPmp] = useState<PmpFormType | undefined>();
  const [defaultPmp, setDefaultPmp] = useState<Pmp>();

  const channelGroupId = pmp?.channelGroupId
    ? pmp.channelGroupId
    : PIC_CHANNEL_ID;
  const spaceOptions = useMemo(() => {
    return channelGroupId ? channelSpaceOptionsMap[channelGroupId] || [] : [];
  }, [channelGroupId, channelSpaceOptionsMap]);

  useEffect(() => {
    callAPIs([() => pmpManager.getPmp(+pmpId)], (pmp: Pmp) => {
      setPmp({
        ...pmp,
        spaces: pmp.spaces.filter((space) =>
          spaceOptions.some((option) => option.value === space)
        )
      });
      setDefaultPmp(pmp);
    });
  }, [pmpId, spaceOptions, callAPIs]);

  const backToList = useCallback(() => {
    setRedirectPath('/pmp');
  }, []);

  const handleSubmit = useCallback(
    (pmp: PmpFormType) => {
      if (!defaultPmp) return;
      callAPIs(
        [
          () =>
            pmpManager.updatePmp({
              ...defaultPmp,
              ...pmp,
              id: +pmpId
            })
        ],
        () => {
          backToList();
          toast.success(i18n.t('pmpSetupFlowPage.labels.updateSuccess'));
        }
      );
    },
    [pmpId, defaultPmp, callAPIs, backToList]
  );

  const readonly = useMemo(() => {
    if (!defaultPmp) {
      return true;
    }

    return !pmpManager.isPmpEditable(defaultPmp);
  }, [defaultPmp]);

  const summaryData = usePmpSummaryModel(pmp, spaceOptions);

  return {
    type: 'edit',
    pmp,
    title: i18n.t('pmpSetupFlowPage.titles.edit'),
    loading,
    summaryData,
    redirectPath,
    readonly,
    haveBoundCampaign: defaultPmp?.campaignId !== undefined,
    setPmp,
    backToList,
    handleSubmit
  };
};

export const useSummaryPmpSetupFlowPageModel = (channelSpaceOptionsMap: {
  [channelId: number]: SelectOptions[];
}): PmpSetupFlowModel => {
  const { loading, callAPIs } = useCallAPI();
  const [redirectPath, setRedirectPath] = useState<string | undefined>();
  const { pmpId } = useParams<{ pmpId: string }>();
  const [pmp, setPmp] = useState<PmpFormType | undefined>();
  const [defaultPmp, setDefaultPmp] = useState<Pmp>();

  const channelGroupId = pmp?.channelGroupId
    ? pmp.channelGroupId
    : PIC_CHANNEL_ID;
  const spaceOptions = useMemo(() => {
    return channelGroupId ? channelSpaceOptionsMap[channelGroupId] || [] : [];
  }, [channelGroupId, channelSpaceOptionsMap]);

  useEffect(() => {
    callAPIs([() => pmpManager.getPmp(+pmpId)], (pmp: Pmp) => {
      setPmp({
        ...pmp,
        spaces: pmp.spaces.filter((space) =>
          spaceOptions.some((option) => option.value === space)
        )
      });
      setDefaultPmp(pmp);
    });
  }, [pmpId, spaceOptions, callAPIs]);

  const backToList = useCallback(() => {
    setRedirectPath('/pmp');
  }, []);

  const summaryData = usePmpSummaryModel(pmp, spaceOptions);

  return {
    type: 'summary',
    pmp,
    title: i18n.t('pmpSetupFlowPage.titles.summary'),
    loading,
    summaryData,
    redirectPath,
    readonly: true,
    haveBoundCampaign: defaultPmp?.campaignId !== undefined,
    setPmp,
    backToList,
    handleSubmit: noop
  };
};
